/**
 * Which barcode should a food created from a label scan end up carrying, and what — if
 * anything — has to be asked before it does? (#4968)
 *
 * A food created this way can be handed TWO barcodes from two different places, and they do not
 * always agree:
 *
 * - the one the USER scanned a moment earlier, which found no food, which is the whole reason
 *   they went on to scan the label. Getting this onto the new food is the point: without it the
 *   same product fails to be found by barcode the next time, and the scan taught the app
 *   nothing.
 * - the one the LABEL SCAN itself read off the package photo. This one is a reading, not a
 *   press: the barcode may be half out of frame, or belong to a multipack, or simply be
 *   misread.
 *
 * Silently letting either win is wrong. One overwriting the other loses a real answer, and
 * asking every time makes the common case — the two agreeing, or only one existing — a chore.
 *
 * This module holds no Angular, no navigation and no dialog, so a plain unit test can ask it
 * every combination. Wording and buttons stay with the page; this only decides what to do.
 */

/** What to ask the user once the page is on screen. */
export enum BarcodeQuestion {
  /** Nothing to ask — either there is no doubt, or there is no barcode at all. */
  None = 'none',
  /**
   * Only the user's own scan produced a barcode. Check the label they photographed really is
   * the product they scanned, since nothing corroborates it.
   */
  ConfirmScanned = 'confirm-scanned',
  /** Both produced a barcode and they disagree. The user picks which one is right. */
  ChooseBetween = 'choose-between',
}

export interface BarcodePairing {
  /**
   * The two candidates, cleaned — the same values `pairNow` is chosen from.
   *
   * They are handed back rather than left to the caller to re-derive, because the caller is
   * the one that offers them as the buttons of the "which of these two?" alert, and a button
   * built from the RAW parameter would store an untrimmed barcode the moment the user picked
   * it. Undefined where that source produced nothing.
   */
  fromLabel?: string;
  fromScan?: string;
  /**
   * The barcode to put on the food straight away, or undefined to leave it with none.
   *
   * Set even when a question is coming. The answer to both questions is usually "yes, the one I
   * scanned", so this is what makes the common case need no repair; a question that is answered
   * the other way replaces it. Pairing first also means a user who never reaches the question —
   * because they left the page — still keeps the barcode they physically scanned.
   */
  pairNow?: string;
  question: BarcodeQuestion;
}

/** Blank, whitespace-only and undefined barcodes all mean "there isn't one". */
function cleaned(barcode: string | undefined | null): string | undefined {
  const trimmed = barcode?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * @param labelBarcode what the label scan read off the package, if anything
 * @param scannedBarcode what the user scanned with the barcode scanner before this, if anything
 */
export function decideBarcodePairing(
  labelBarcode: string | undefined | null,
  scannedBarcode: string | undefined | null,
): BarcodePairing {
  const fromLabel = cleaned(labelBarcode);
  const fromScan = cleaned(scannedBarcode);

  // if the user never scanned a barcode, the label's reading is the only answer there is, and
  // pairing it silently is what this has always done
  if (!fromScan) {
    return { fromLabel, fromScan, pairNow: fromLabel, question: BarcodeQuestion.None };
  }

  // if the label produced nothing to compare against, take the user's scan but check it belongs
  // to the label they photographed
  if (!fromLabel) {
    return { fromLabel, fromScan, pairNow: fromScan, question: BarcodeQuestion.ConfirmScanned };
  }

  // if both agree there is nothing to decide, and asking would be noise
  if (fromLabel === fromScan) {
    return { fromLabel, fromScan, pairNow: fromScan, question: BarcodeQuestion.None };
  }

  // they disagree, so the user decides. The scanned one is offered as the standing answer
  // because they produced it deliberately, by pointing the scanner at the product.
  return { fromLabel, fromScan, pairNow: fromScan, question: BarcodeQuestion.ChooseBetween };
}
